<template>
  <div
    id="overview-map"
    class="map" />
</template>

<script setup lang="ts">
  import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

  import mapboxgl, { type ControlPosition, type LngLatLike, type MapOptions } from 'mapbox-gl';
  import { onMounted, ref, watch } from 'vue';

  import Colors from '@/Colors';
  import { MapInventoryType } from '@/common/enums/map.enums';
  import { Market, Unit } from '@/common/models';
  import GeofenceGroup from '@/common/models/geo/GeofenceGroup';
  import LocationShape from '@/common/models/geo/LocationShape';
  import {
    debounce,
    flattenGeoJsonGeometry,
    getBounds as globalGetBounds,
    getContainerHeight,
    getContainerWidth,
  } from '@/globals';
  import MapStyleControl from '@/org-app/components/geofenceGroups/MapStyleControl';
  import MapCart from '@/org-app/components/inventory/overallMap/MapCart.js';
  import formatNumber from '@/utils/NumberFormatter';
  import DOMPurify from 'dompurify';

  const GeofencePinBase64Image =
    'data:image/png;base64,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';

  // Type
  interface IGeocodedLocationsGeoJson {
    type: string;
    features: Array<{
      type: string;
      geometry: {
        type: string;
        coordinates: [number, number];
      };
      id: number;
      properties: Record<string, unknown>;
    }>;
  }

  interface IProps {
    cart: MapCart;
    centroidFeatures: number[];
    geofenceGroupPolygonFeatures: GeofenceGroup[];
    geofenceGroupCentroidFeatures: GeofenceGroup[];
    fitBoundsRightPadding: number;
    geocodedLocationsGeoJson: IGeocodedLocationsGeoJson;
    units: Unit[];
    marketFilters: Market[];
    marketPolygonFeatures: Market[];
    locationFilters: Location[];
    locationPolygonFeatures: LocationShape[];
    geofenceGroupFilters: GeofenceGroup[];
    isReferenceDataReady: boolean;
    mapControlPosition: ControlPosition;
    showNetworks: boolean;
    uniqueNetworkIDs: number[];
    clusteredType: MapInventoryType;
    colorPerNetworkID: { id: string; n: number; color: string }[];
    activeLocationMinDistanceFromLeft: number;
  }

  // Props
  const props = withDefaults(defineProps<IProps>(), {
    cart: null,
    centroidFeatures: () => [],
    geofenceGroupPolygonFeatures: () => [],
    geofenceGroupCentroidFeatures: () => [],
    fitBoundsRightPadding: 24,
    geocodedLocationsGeoJson: null,
    units: () => [],
    marketFilters: () => [],
    marketPolygonFeatures: () => [],
    locationFilters: () => [],
    locationPolygonFeatures: () => [],
    geofenceGroupFilters: () => [],
    isReferenceDataReady: false,
    mapControlPosition: 'top-right',
    showNetworks: true,
    uniqueNetworkIDs: () => [],
    colorPerNetworkID: () => [],
    activeLocationMinDistanceFromLeft: 0,
  });

  // Emits
  const emits = defineEmits<{
    (e: 'mapLoaded'): void;
    (e: 'clearActiveUnits'): void;
    (e: 'closeMapPane'): void;
    (e: 'fitBoundsTriggered'): void;
    (e: 'activeUnitIDsChanged', items: { type: MapInventoryType; ids: number[] }): void;
    (e: 'requestLocationsUpdate', fitToBounds: boolean): void;
  }>();

  // Data
  const map = ref(null);
  const timeout = ref(null);
  const isMapLoaded = ref(false);
  const mapStyleControl = ref(null);
  const donutMarkersOnScreen = ref([]);
  const didFitToBoundsOnce = ref(false);
  const isMapStyleSatellite = ref(false);
  const isTriggeredFromFitBounds = ref(false);

  // Methods
  function initMap(mapOptions) {
    const mapInternal = new mapboxgl.Map(mapOptions);
    mapInternal.on('load', () => {
      isMapLoaded.value = true;

      map.value.loadImage(GeofencePinBase64Image, (error, img) => {
        if (error == null) {
          if (!map.value.hasImage('pin')) {
            map.value.addImage('pin', img);
          }
        }
      });
      mapStyleControl.value = new MapStyleControl();
      mapStyleControl.value.clickHandler = (isSatellite) => {
        if (isSatellite) {
          changeMapStyleToSatellite();
        } else {
          changeMapStyleToLight();
        }
      };
      map.value.addControl(mapStyleControl.value, props.mapControlPosition);
      updateMapDatasourcesAndLayers(true);
      updateMapGeographyFilterPolygons();
      map.value.resize();
      emits('mapLoaded');
    });

    mapInternal.addControl(new mapboxgl.NavigationControl(), props.mapControlPosition);
    mapInternal.addControl(new mapboxgl.ScaleControl());
    mapInternal.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      }),
      props.mapControlPosition,
    );

    mapInternal.on('click', () => {
      emits('clearActiveUnits');
      emits('closeMapPane');
      clearActiveSiteOrUnitFromMap();
    });

    mapInternal.on('zoomend', debouncedRequestLocationsUpdate);
    mapInternal.on('touchend', debouncedRequestLocationsUpdate);
    mapInternal.on('dragend', debouncedRequestLocationsUpdate);
    mapInternal.on('resize', debouncedRequestLocationsUpdate);

    return mapInternal;
  }

  function debouncedRequestLocationsUpdate() {
    debounce(
      () => {
        if (isTriggeredFromFitBounds.value) {
          isTriggeredFromFitBounds.value = false;
          emits('fitBoundsTriggered');
        }
        requestLocationsUpdate(false);
        map.value.resize();
      },
      200,
      timeout.value,
    )();
  }

  function requestLocationsUpdate(fitToBounds: boolean = false) {
    emits('requestLocationsUpdate', fitToBounds);
  }

  function getUnitsAndSitesBounds() {
    const bounds = globalGetBounds(props.units.map((x) => [x.longitude, x.latitude])) as [LngLatLike, LngLatLike];
    if (bounds != null) {
      return new mapboxgl.LngLatBounds(bounds);
    }
    return null;
  }

  function getMarketsBounds(list = props.marketFilters) {
    const flattenedMarketCoordinates = flattenGeoJsonGeometry(list.map((x) => x.geometry));
    const bounds = globalGetBounds(flattenedMarketCoordinates) as [LngLatLike, LngLatLike];
    if (bounds != null) {
      return new mapboxgl.LngLatBounds(bounds);
    }
    return null;
  }

  function getGeofenceGroupsBounds(list = props.geofenceGroupFilters) {
    // Extend bounds by geofenceGroup bounds.
    if (list.length > 0) {
      const flattenedGeofenceGroupCoordinates = list
        .filter((gg) => gg.geofences.length > 0)
        .map((gg) =>
          gg.geofences
            .map((g) => [g.boundingBox.slice(0, 2), g.boundingBox.slice(2, 4)])
            .reduce((mem, x) => [...mem, ...x], []),
        )
        .reduce((mem, x) => [...mem, ...x], []);
      const bounds = globalGetBounds(flattenedGeofenceGroupCoordinates) as [LngLatLike, LngLatLike];
      if (bounds != null) {
        return new mapboxgl.LngLatBounds(bounds);
      }
    }
    return null;
  }

  function getLocationsBounds(list = props.locationPolygonFeatures) {
    const flattenedCoordinates = flattenGeoJsonGeometry(list.map((x) => x.geometry));
    const bounds = globalGetBounds(flattenedCoordinates) as [LngLatLike, LngLatLike];
    if (bounds != null) {
      return new mapboxgl.LngLatBounds(bounds);
    }
    return null;
  }

  async function fitToBoundsOfAllLocations() {
    let bounds = null;

    const unitsAndSitesBounds = getUnitsAndSitesBounds();
    if (unitsAndSitesBounds != null) {
      if (bounds != null) {
        bounds.extend(unitsAndSitesBounds);
      } else {
        bounds = unitsAndSitesBounds;
      }
    }

    const marketFiltersBounds = getMarketsBounds();
    if (marketFiltersBounds != null) {
      if (bounds != null) {
        bounds.extend(marketFiltersBounds);
      } else {
        bounds = marketFiltersBounds;
      }
    }

    const geofenceFiltersBounds = getGeofenceGroupsBounds();
    if (geofenceFiltersBounds != null) {
      if (bounds != null) {
        bounds.extend(geofenceFiltersBounds);
      } else {
        bounds = geofenceFiltersBounds;
      }
    }

    const locationFiltersBounds = getLocationsBounds();
    if (locationFiltersBounds != null) {
      if (bounds != null) {
        bounds.extend(locationFiltersBounds);
      } else {
        bounds = locationFiltersBounds;
      }
    }
    fitBounds(bounds);
  }

  function fitBounds(bounds) {
    if (bounds != null) {
      isTriggeredFromFitBounds.value = true;
      map.value.stop();
      map.value.fitBounds(bounds, {
        padding: {
          top: 64,
          bottom: 64,
          left: 64,
          right: props.fitBoundsRightPadding,
        },
        duration: 250,
      });
    }
  }

  function changeMapStyleToSatellite() {
    const mapStyle = 'mapbox://styles/mapbox/satellite-streets-v9?optimize=true';
    map.value.setStyle(mapStyle);
    isMapStyleSatellite.value = true;
    setTimeout(() => {
      updateMapDatasourcesAndLayers(false, true);
      updateMapGeographyFilterPolygons();
    }, 1000);
  }

  function changeMapStyleToLight() {
    const mapStyle = 'mapbox://styles/mapbox/light-v9?optimize=true';
    map.value.setStyle(mapStyle);
    isMapStyleSatellite.value = true;
    setTimeout(() => {
      updateMapDatasourcesAndLayers(false, true);
      updateMapGeographyFilterPolygons();
    }, 1000);
  }

  function updateMapDatasourcesAndLayers(fitToBounds = true, forceLayerUpdate = false) {
    if (!isMapLoaded.value || !props.isReferenceDataReady || !props.geocodedLocationsGeoJson) {
      return;
    }
    const wasMapInitialized = map.value.getSource('geocodedLocations') !== undefined;
    if (wasMapInitialized) {
      map.value.getSource('geocodedLocations').setData(props.geocodedLocationsGeoJson);
      emits('fitBoundsTriggered');
      if (props.showNetworks) {
        setTimeout(() => {
          updateDonutMarkers();
          map.value.triggerRepaint();
        }, 100);
      }
    } else {
      map.value.addSource('geocodedLocations', {
        type: 'geojson',
        data: props.geocodedLocationsGeoJson,
      });
      map.value.addSource('activeUnitOrSite', {
        type: 'geojson',
        data: null,
      });

      const marketPolygonsData = {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      };
      map.value.addSource('market_polygons_source', marketPolygonsData);

      const geofenceGroupPolygonsData = {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      };
      map.value.addSource('geofencegroup_polygons_source', geofenceGroupPolygonsData);
      map.value.addSource('geofencegroup_centroids_source', geofenceGroupPolygonsData);
      map.value.addSource('proximity_search_polygons_source', geofenceGroupPolygonsData);
      map.value.addSource('proximity_search_centroids_source', geofenceGroupPolygonsData);

      const locationPolygonsData = {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      };
      map.value.addSource('location_polygons_source', locationPolygonsData);

      const activePolygonsData = {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      };
      map.value.addSource('active_polygons_source', activePolygonsData);
    }

    if (forceLayerUpdate) {
      if (map.value.getLayer('3d-buildings') != null) {
        map.value.removeLayer('3d-buildings');
        map.value.removeLayer('clusters');
        map.value.removeLayer('cluster-count');
        map.value.removeLayer('unclustered-point');
      }
    }

    if (map.value.getLayer('3d-buildings') == null) {
      map.value.addLayer({
        id: '3d-buildings',
        source: 'composite',
        'source-layer': 'building',
        filter: ['==', 'extrude', 'true'],
        type: 'fill-extrusion',
        minzoom: 15,
        paint: {
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-height': {
            type: 'identity',
            property: 'height',
          },
          'fill-extrusion-base': {
            type: 'identity',
            property: 'min_height',
          },
          'fill-extrusion-opacity': 0.6,
        },
      });

      map.value.addLayer({
        id: 'active-polygon-fill',
        type: 'fill',
        source: 'active_polygons_source',
        paint: {
          'fill-opacity': isMapStyleSatellite.value ? 0.2 : 0.05,
          'fill-color': Colors.secondary,
        },
      });
      map.value.addLayer({
        id: 'active-polygon-line',
        type: 'line',
        source: 'active_polygons_source',
        paint: {
          'line-opacity': isMapStyleSatellite.value ? 0.75 : 0.35,
          'line-color': Colors.secondary,
        },
      });

      //
      // Market layers
      //
      map.value.addLayer({
        id: 'market-polygon-fill',
        type: 'fill',
        source: 'market_polygons_source',
        paint: {
          'fill-opacity': isMapStyleSatellite.value ? 0.2 : 0.05,
          'fill-color': ['match', ['get', 'isExcluded'], 'true', Colors.error, 'false', Colors.success, Colors.success],
        },
      });
      map.value.addLayer({
        id: 'market-polygon-line',
        type: 'line',
        source: 'market_polygons_source',
        paint: {
          'line-opacity': isMapStyleSatellite.value ? 0.75 : 0.35,
          'line-color': ['match', ['get', 'isExcluded'], 'true', Colors.error, 'false', Colors.success, Colors.success],
        },
      });

      //
      // Geofences layers
      //
      map.value.addLayer({
        id: 'geofencegroup-polygon-fill',
        type: 'fill',
        source: 'geofencegroup_polygons_source',
        paint: {
          'fill-opacity': isMapStyleSatellite.value ? 0.2 : 0.05,
          'fill-color': Colors.geofences,
        },
      });
      map.value.addLayer({
        id: 'geofencegroup-polygon-line',
        type: 'line',
        source: 'geofencegroup_polygons_source',
        paint: {
          'line-opacity': isMapStyleSatellite.value ? 0.75 : 0.35,
          'line-color': Colors.geofences,
        },
      });
      map.value.addLayer({
        id: 'geofencegroup-polygon-markers',
        type: 'symbol',
        source: 'geofencegroup_centroids_source',
        layout: {
          'icon-image': 'pin',
          'icon-size': 0.5,
          'icon-allow-overlap': true,
        },
      });

      //
      // Proximity search layers
      //
      map.value.addLayer({
        id: 'proximity-search-polygon-fill',
        type: 'fill',
        source: 'proximity_search_polygons_source',
        paint: {
          'fill-opacity': isMapStyleSatellite.value ? 0.2 : 0.05,
          'fill-color': Colors.secondary,
        },
      });
      map.value.addLayer({
        id: 'proximity-search-polygon-line',
        type: 'line',
        source: 'proximity_search_polygons_source',
        paint: {
          'line-opacity': isMapStyleSatellite.value ? 0.75 : 0.35,
          'line-color': Colors.secondary,
        },
      });
      map.value.addLayer({
        id: 'proximity-search-polygon-markers',
        type: 'symbol',
        source: 'proximity_search_centroids_source',
        layout: {
          'icon-image': 'pin',
          'icon-size': 0.5,
          'icon-allow-overlap': true,
        },
      });

      //
      // Locations (city/region/country) layers
      //
      map.value.addLayer({
        id: 'location-polygon-fill',
        type: 'fill',
        source: 'location_polygons_source',
        paint: {
          'fill-opacity': isMapStyleSatellite.value ? 0.2 : 0.05,
          'fill-color': Colors.geofences,
        },
      });
      map.value.addLayer({
        id: 'location-polygon-line',
        type: 'line',
        source: 'location_polygons_source',
        paint: {
          'line-opacity': isMapStyleSatellite.value ? 0.75 : 0.35,
          'line-color': Colors.geofences,
        },
      });

      //
      // Clusters layers
      //
      map.value.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'geocodedLocations',
        filter: ['>', 'count', 1],
        paint: {
          'circle-color': ['case', ['boolean', ['get', 'isActive'], false], Colors.success, Colors.actions],
          'circle-opacity': 0.9,
          'circle-opacity-transition': { duration: 0 },
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1,
          'circle-stroke-opacity': 1,
          'circle-pitch-alignment': 'map',
          'circle-radius': {
            property: 'count',
            type: 'interval',
            stops: [
              [0, 20],
              [100, 25],
              [750, 30],
            ],
          },
        },
      });

      map.value.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'geocodedLocations',
        filter: props.showNetworks ? ['has', 'siteCount'] : ['>', 'count', 1],
        layout: {
          'text-field': props.showNetworks ? ['get', 'siteCount'] : '{countFormatted}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
          'text-size': props.showNetworks ? 25 : 15,
        },
        paint: {
          'text-color': props.showNetworks ? 'transparent' : '#fff',
        },
      });

      //
      // Non-clustered screens.
      //
      map.value.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'geocodedLocations',
        filter: ['==', 'count', 1],
        paint: {
          'circle-color': ['case', ['boolean', ['get', 'isActive'], false], Colors.success, Colors.actions],
          'circle-radius': 4,
          'circle-stroke-width': ['match', ['get', 'isSelected'], 'true', 2, 'false', 2, 2],
          'circle-stroke-opacity': 0.5,
          'circle-stroke-color': 'rgba(255,255,255,0.5)',
          'circle-pitch-alignment': 'map',
          'circle-opacity-transition': { duration: 0 },
        },
      });

      const handleClusterClick = (e) => {
        const [feature] = e.features;
        if (props.showNetworks) {
          const siteIDs = JSON.parse(feature.properties.siteIDs);
          if (siteIDs.length > 15 && map.value.getZoom() < 21) {
            map.value.easeTo({
              center: [e.lngLat.lng, e.lngLat.lat],
              zoom: map.value.getZoom() + 3,
              duration: 1000,
            });
            debouncedRequestLocationsUpdate();
          } else {
            const centerRightLng = e.lngLat.lng - Math.abs(e.lngLat.lng - map.value.getBounds()._ne.lng) / 3;
            map.value.easeTo({
              center: [centerRightLng, e.lngLat.lat],
              zoom: map.value.getZoom(),
              duration: 1000,
            });
            const items = {
              type: MapInventoryType.SITES,
              ids: siteIDs,
            };
            emits('activeUnitIDsChanged', items);
          }
        } else if (
          map.value.getZoom() < feature.properties.expansionZoom &&
          feature.properties.expansionZoom < 22 &&
          map.value.getZoom() < 22
        ) {
          const bounds = JSON.parse(feature.properties.bounds);
          const marginH = getContainerWidth('.overview-map-container') / 5;
          const marginV = getContainerHeight('.overview-map-container') / 5;
          map.value.fitBounds(
            [
              [bounds.min_lon, bounds.min_lat],
              [bounds.max_lon, bounds.max_lat],
            ],
            {
              padding: {
                left: marginH,
                right: marginH,
                top: marginV,
                bottom: marginV,
              },
              duration: 250,
              animate: true,
            },
          );
        } else {
          if (e.point.x < props.activeLocationMinDistanceFromLeft) {
            map.value.easeTo({
              center: [feature.properties.lng, feature.properties.lat],
              duration: 100,
            });
          }
          setTimeout(() => {
            const unitIDs = [feature].reduce(
              (mem, x) => (x.properties.unitIDs ? [...mem, ...JSON.parse(x.properties.unitIDs)] : mem),
              [],
            );
            const siteIDs = [feature].reduce(
              (mem, x) => (x.properties.siteIDs ? [...mem, ...JSON.parse(x.properties.siteIDs)] : mem),
              [],
            );
            const items = {
              type: unitIDs?.length ? MapInventoryType.UNITS : MapInventoryType.SITES,
              ids: unitIDs?.length ? unitIDs : siteIDs,
            };
            emits('activeUnitIDsChanged', items);
          }, 10);
        }
      };

      map.value.on('click', 'geofencegroup-polygon-markers', (e) => {
        const [feature] = e.features;
        const latLng = feature.geometry.coordinates;
        new mapboxgl.Popup({ closeOnClick: true })
          .setLngLat(latLng)
          .setHTML(
            `<strong>${DOMPurify.sanitize(feature.properties.name, { USE_PROFILES: { html: false } })}</strong><br>` +
              `<span>${DOMPurify.sanitize(feature.properties.description, { USE_PROFILES: { html: false } })}</span>`,
          )
          .addTo(map.value);
      });

      map.value.on('click', 'clusters', (e) => {
        handleClusterClick(e);
      });

      map.value.on('click', 'cluster-count', (e) => {
        handleClusterClick(e);
      });

      map.value.on('click', 'unclustered-point', (e) => {
        const [feature] = e.features;
        if (e.point.x < props.activeLocationMinDistanceFromLeft) {
          map.value.easeTo({
            center: [feature.properties.lng, feature.properties.lat],
            duration: 0,
          });
        }
        const unitIDs = [feature].reduce(
          (mem, x) => (x.properties.unitIDs ? [...mem, ...JSON.parse(x.properties.unitIDs)] : mem),
          [],
        );
        const siteIDs = [feature].reduce(
          (mem, x) => (x.properties.siteIDs ? [...mem, ...JSON.parse(x.properties.siteIDs)] : mem),
          [],
        );
        const items = {
          type: unitIDs?.length ? MapInventoryType.UNITS : MapInventoryType.SITES,
          ids: unitIDs?.length ? unitIDs : siteIDs,
        };
        emits('activeUnitIDsChanged', items);
      });
    }

    //
    // Zoom on locations.
    //
    if (!didFitToBoundsOnce.value || fitToBounds) {
      didFitToBoundsOnce.value = true;
      fitToBoundsOfAllLocations();
    }

    updateMapGeographyFilterPolygons();
  }

  async function updateMarketPolygonsDatasource() {
    const marketPolygonsLayerData = map.value.getSource('market_polygons_source');
    if (marketPolygonsLayerData === undefined) {
      return;
    }
    marketPolygonsLayerData.setData({
      type: 'FeatureCollection',
      features: props.marketPolygonFeatures,
    });
  }

  function updateGeofenceGroupPolygonsDatasource() {
    if (map.value == null) {
      return;
    }
    const geofenceGroupPolygonsLayerData = map.value.getSource('geofencegroup_polygons_source');
    const geofenceGroupCentroidsLayerData = map.value.getSource('geofencegroup_centroids_source');
    if (geofenceGroupPolygonsLayerData === undefined) {
      return;
    }
    geofenceGroupPolygonsLayerData.setData({
      type: 'FeatureCollection',
      features: props.geofenceGroupPolygonFeatures,
    });
    geofenceGroupCentroidsLayerData.setData({
      type: 'FeatureCollection',
      features: props.geofenceGroupCentroidFeatures,
    });
  }

  function updateGeofencePolygonsFromProximitySearchDatasource() {
    if (map.value == null) {
      return;
    }
    const polygonsLayerData = map.value.getSource('proximity_search_polygons_source');
    const centroidsLayerData = map.value.getSource('proximity_search_centroids_source');
    if (polygonsLayerData === undefined) {
      return;
    }
    centroidsLayerData.setData({
      type: 'FeatureCollection',
      features: props.centroidFeatures,
    });
  }

  function updateLocationPolygonsDatasource() {
    const locationPolygonsLayerData = map.value.getSource('location_polygons_source');
    if (locationPolygonsLayerData === undefined) {
      return;
    }
    locationPolygonsLayerData.setData({
      type: 'FeatureCollection',
      features: props.locationPolygonFeatures,
    });
  }

  async function updateMapGeographyFilterPolygons() {
    if (map.value == null || !isMapLoaded.value) {
      return;
    }
    await updateMarketPolygonsDatasource();
    updateLocationPolygonsDatasource();
    updateGeofenceGroupPolygonsDatasource();
    updateGeofencePolygonsFromProximitySearchDatasource();
  }

  function showActiveFeatures(features) {
    // Delay the call until the map is ready.
    if (map.value == null) {
      setTimeout(showActiveFeatures, 100);
      return;
    }

    const activePolygonsLayerData = map.value.getSource('active_polygons_source');
    if (activePolygonsLayerData === undefined) {
      return;
    }
    activePolygonsLayerData.setData({
      type: 'FeatureCollection',
      features,
    });
  }

  function getZoomLevel() {
    return map.value.transform.zoom;
  }

  function setZoomLevel(zoomLevel) {
    map.value.flyTo({
      zoom: zoomLevel,
    });
  }

  function defaultZoom() {
    map.value.easeTo({
      center: [0, 0],
      zoom: 2,
      duration: 1000,
    });
  }

  function displayActiveSiteOrUnitOnMap(siteOrUnit) {
    if (map.value != null && map.value.getSource('activeUnitOrSite') != null) {
      map.value.getSource('activeUnitOrSite').setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [siteOrUnit.longitude, siteOrUnit.latitude],
            },
            properties: {
              id: siteOrUnit.id,
              name: siteOrUnit.displayName,
            },
          },
        ],
      });
    }
  }

  function clearActiveSiteOrUnitFromMap() {
    if (map.value != null && map.value.getSource('activeUnitOrSite') != null) {
      map.value.getSource('activeUnitOrSite').setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }

  function getBounds() {
    const bounds = map.value.getBounds();
    return [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
  }

  function getZoom() {
    return map.value.getZoom();
  }

  function enableMouseScrollZoom() {
    map.value.scrollZoom.enable();
  }

  function disableMouseScrollZoom() {
    map.value.scrollZoom.disable();
  }

  function resize() {
    map.value.resize();
  }

  function updateDonutMarkers() {
    const { features } = props.geocodedLocationsGeoJson;

    // Clean previous markers
    donutMarkersOnScreen.value.forEach((m) => m.remove());
    donutMarkersOnScreen.value = [];

    // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
    // and add it to the map if it's not there already
    for (const feature of features) {
      const coords = feature.geometry.coordinates;
      const props = feature.properties;
      const el = createDonutChart(props);
      const marker = new mapboxgl.Marker({
        element: el,
      })
        .setLngLat(coords)
        .addTo(map.value);
      donutMarkersOnScreen.value.push(marker);
    }
  }

  function createDonutChart(propsInternal): HTMLElement {
    const siteCountForCluster = propsInternal.siteCount;
    const networkIDs = Object.keys(propsInternal.networks);
    const networkCounts = Object.values(propsInternal.networks);

    /** Get network IDs and their count for knowing how many categories we got and if we need to show the top 7 only.
     *  Also sorts the array based on their count so that we always show the highest number of networks first.
     * @type {{count: number, id: *}[]}
     */
    const networks = networkIDs
      .map((networkID, index) => ({
        id: networkID,
        count: +networkCounts[index],
      }))
      .sort((a, b) => b.count - a.count);

    const total = networks.reduce((mem, n) => mem + n.count, 0);
    const maxNetworkSlices = 16;

    const clusterSiteCount = siteCountForCluster;
    const fontSize = 14;
    const r = clusterSiteCount >= 1000 ? 32 : clusterSiteCount >= 100 ? 28 : clusterSiteCount >= 10 ? 24 : 18;
    const r0 = Math.round(r * 0.7);
    const w = r * 2;

    let html = `<div>
                  <svg width="${w}" height="${w}" viewbox="0 0 ${w} ${w}" text-anchor="middle">`;
    let offset = 0;
    for (let i = 0; i < networks.length; i += 1) {
      let { count } = networks[i];
      const networkColor = props.colorPerNetworkID.find((x) => x.id == networks[i].id);
      if (i === maxNetworkSlices - 1) {
        count = networks.slice(i).reduce((mem, n) => mem + n.count, 0);
      }

      html += donutSegment(
        offset / total,
        (offset + count) / total,
        r,
        r0,
        i === maxNetworkSlices - 1 || networkColor == null ? '#777777' : networkColor.color,
      );
      offset += networks[i].count;

      if (i === maxNetworkSlices - 1) {
        break;
      }
    }
    html += `<circle cx="${r}" cy="${r}" r="${r0}" fill="white" />
              <text dominant-baseline="central"
                    transform="translate(${r}, ${r})"\
                    font-size="${fontSize}px"
                    font-weight="600"
                    fill="#000000e6"
                    font-family="Oxygen", Helvetica, Arial, sans-serif;">
                      ${clusterSiteCount < 1000 ? clusterSiteCount.toLocaleString() : formatNumber(clusterSiteCount, '0.0a')}
              </text>
              </svg>
              </div>`;

    const el = document.createElement('div');
    el.innerHTML = html;
    return el.firstChild as HTMLElement;
  }

  function donutSegment(start, end, r, r0, color) {
    if (end - start === 1) end -= 0.00001;
    const a0 = 2 * Math.PI * (start - 0.25);
    const a1 = 2 * Math.PI * (end - 0.25);
    const x0 = Math.cos(a0);
    const y0 = Math.sin(a0);
    const x1 = Math.cos(a1);
    const y1 = Math.sin(a1);
    const largeArc = end - start > 0.5 ? 1 : 0;

    // draw an SVG path
    return `<path d="M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${
      r + r * y0
    } A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${
      r + r0 * x1
    } ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${r + r0 * y0}" fill="${color}" />`;
  }

  function initializeMap() {
    const mapOptions: MapOptions = {
      container: 'overview-map',
      style: 'mapbox://styles/mapbox/light-v9?optimize=true',
      minZoom: 1.3,
      zoom: map.value?.getZoom() || 2,
    };

    const center = map.value?.getCenter() || null;
    if (center) mapOptions.center = Object.values(center) as LngLatLike;

    // Init map
    const initMapInternal = () => {
      if (window.document.getElementById('overview-map') == null) {
        setTimeout(initMap, 100);
        return;
      }
      map.value = initMap(mapOptions);
    };
    initMapInternal();
  }

  // Watchers
  watch(isMapLoaded, () => {
    if (!props.showNetworks) {
      updateMapDatasourcesAndLayers();
    }
  });

  watch(
    () => props.isReferenceDataReady,
    () => {
      if (!props.showNetworks) {
        updateMapDatasourcesAndLayers();
      }
    },
  );

  watch(
    () => props.geofenceGroupPolygonFeatures,
    () => {
      updateGeofenceGroupPolygonsDatasource();
    },
  );

  watch(
    () => props.geofenceGroupCentroidFeatures,
    () => {
      updateGeofenceGroupPolygonsDatasource();
    },
  );

  watch(
    () => props.centroidFeatures,
    () => {
      updateGeofencePolygonsFromProximitySearchDatasource();
    },
  );

  watch(
    () => props.marketPolygonFeatures,
    () => {
      updateMarketPolygonsDatasource();
      fitToBoundsOfAllLocations();
    },
  );

  watch(
    () => props.locationPolygonFeatures,
    () => {
      updateLocationPolygonsDatasource();
      fitToBoundsOfAllLocations();
    },
  );

  watch(
    () => props.geocodedLocationsGeoJson,
    () => {
      updateMapDatasourcesAndLayers(false);
    },
  );

  watch(
    () => props.clusteredType,
    (val) => {
      if (val === MapInventoryType.UNITS) {
        donutMarkersOnScreen.value.forEach((m) => m.remove());
        donutMarkersOnScreen.value = [];
      } else {
        map.value.removeLayer('3d-buildings');
        map.value.removeLayer('clusters');
        map.value.removeLayer('active-polygon-fill');
        map.value.removeLayer('active-polygon-line');
        map.value.removeLayer('market-polygon-fill');
        map.value.removeLayer('market-polygon-line');
        map.value.removeLayer('location-polygon-fill');
        map.value.removeLayer('location-polygon-line');
      }
      map.value.removeLayer('cluster-count');
      map.value.removeLayer('unclustered-point');
      map.value.removeLayer('geofencegroup-polygon-fill');
      map.value.removeLayer('geofencegroup-polygon-line');
      map.value.removeLayer('geofencegroup-polygon-markers');
      map.value.removeLayer('proximity-search-polygon-fill');
      map.value.removeLayer('proximity-search-polygon-line');
      map.value.removeLayer('proximity-search-polygon-markers');
      isMapLoaded.value = false;
      initializeMap();
    },
  );

  // Lifecycle Hooks
  onMounted(() => {
    initializeMap();
  });

  // Expose
  defineExpose({
    getZoom,
    clearActiveSiteOrUnitFromMap,
    showActiveFeatures,
    displayActiveSiteOrUnitOnMap,
    setZoomLevel,
    enableMouseScrollZoom,
    disableMouseScrollZoom,
    fitBounds,
    getZoomLevel,
    getBounds,
    defaultZoom,
    resize,
    updateGeofenceGroupPolygonsDatasource,
  });
</script>

<style scoped>
  .mapboxgl-popup {
    width: 100%;
    max-width: 333px;
  }
</style>
